import { NavigateFunction } from 'react-router'
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format currency value with proper locale and currency code
 * @param value - Amount in minor units (e.g., cents)
 * @param currencyCode - ISO currency code (e.g., 'USD', 'EUR')
 * @param locale - Locale string (e.g., 'en-US', 'de-DE')
 * @returns Formatted currency string
 */
export function formatCurrency(value: number, currencyCode: string, locale?: string) {
  const majorUnits = Math.round(value / 100); // Round to nearest whole unit
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 2, // Ensure no decimal places
    }).format(majorUnits);
  } catch (e: any) {
    console.error(e);
    // Fallback with no decimals
    return majorUnits.toFixed(0);
  }
}

/**
 * Navigate to a route and replace the current history entry
 * This prevents back navigation to authentication pages
 */
export function navigateWithReplace(router: NavigateFunction, path: string) {
  if (typeof window !== 'undefined') {
    router(path, {
      replace: true
    });
  }
}

/**
 * Clear browser history and navigate to a new page
 * This ensures users can't go back to previous authentication states
 */
export function clearHistoryAndNavigate(router: NavigateFunction, path: string) {
  if (typeof window !== 'undefined') {
    // Replace current history entry
    router(path, {
      replace: true
    });

    // Clear any remaining history entries for auth pages
    const currentPath = window.location.pathname;
    if (currentPath.includes('/signin') || currentPath.includes('/otp-verification')) {
      // Force a clean history state
      window.history.replaceState(null, '', path);
    }
  }
}

export function logOut() {
  localStorage.removeItem('user');
  localStorage.removeItem('token');
  localStorage.removeItem('shopping-cart');
}

export type DeviceType = 'mobile' | 'tablet' | 'desktop';

/**
 * Determines the device type based on viewport width.
 * 
 * Mobile: <= 767px  
 * Tablet: 768px – 1024px  
 * Desktop: >= 1025px
 */
export function getDeviceType(): DeviceType {
  const width = window.innerWidth;

  if (width <= 767) return 'mobile';
  if (width <= 1024) return 'tablet';
  return 'desktop';
}
